/* =============================================================================
   VIEWS / ADMIN / RECEIPT-STANDALONE — der Bon als eigenständiges HTML-Dokument
   =============================================================================
   Herausgelöst in S254 aus `orders-receipt-actions.js`, unverändert im Inhalt.

   Grund für den Schnitt: seit S254 baut auch die GAST-Seite am Tisch diesen Bon
   (Jonas: „Denselben Bon wie überall sonst"). Sie hängt damit an genau diesem
   einen Baustein — nicht am Cockpit-Modul drumherum, das Beleg-Mails, PDF-Anhang
   und Bon-Archiv mitbringt. Ein anonymer Gast braucht davon nichts, und ein
   Import zöge es trotzdem mit.

   `orders-receipt-actions.js` re-exportiert die Funktion, damit kein bestehender
   Aufrufer etwas merkt.

   Das Ergebnis ist ein VOLLSTÄNDIGES HTML-Dokument mit eigenem `<style>`. Es
   darf deshalb NIE per `innerHTML` in eine laufende Seite gesetzt werden — die
   Regeln würden global gelten (der S230-Fehler). Im Cockpit landet es in einem
   eigenen Fenster bzw. im Archiv, auf der Gast-Seite in einem `<iframe>`.
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import { renderOrderReceipt } from './receipt.js';

/** Baut ein Standalone-HTML-Dokument mit eingebettetem Style fürs Archiv.
 *  Async (S54): injiziert das QR-SVG vorab in den Body, damit der QR auch
 *  in archivierten / heruntergeladenen / gemailten HTML-Bons sichtbar ist
 *  (keine JS-Ausführung in diesen Kontexten). */
export async function buildStandaloneReceiptHtml(order, opts) {
  let body = renderOrderReceipt(order, opts);
  // S54 — QR-SVG vorab in den Body inlinen
  const payload = order && (order.tseQrPayload || order.tse_qr_payload);
  if (payload && body.includes('receipt-tse-qr')) {
    try {
      const { generateQrSvg } = await import('../../utils/qr-code.js');
      const svg = await generateQrSvg(payload, { cellSize: 4, margin: 2, errorLevel: 'M' });
      // Placeholder ersetzen — exakter Match auf den HTML-Schnipsel aus renderTseBlock
      body = body.replace(
        /<div class="receipt-tse-qr-placeholder"[^>]*>[\s\S]*?<\/div>/,
        svg
      );
    } catch (qrErr) {
      console.warn('[buildStandaloneReceiptHtml] QR-Inline fehlgeschlagen, Klartext bleibt:', qrErr);
    }
  }
  const title = `Beleg ${order.invoiceNumber || order.orderNumber || order.id}`;
  // Minimal-Style — Bondrucker-Optik (monospace) und Web-Optik in einem.
  const css = `
    /* S183 — Bon ist eine GEWOLLTE helle Papier-Insel (Memory gg-darkmode-invariants).
       color-scheme:light + opake helle Flächen verhindern, dass Dark-Mode-Mail-Clients
       (Apple Mail etc.) die Adress-/USt-/Meta-Zeilen unleserlich invertieren. */
    body { font-family: system-ui, -apple-system, sans-serif; background: #f4f1ea; margin: 0; padding: 24px; color: #1a2622; color-scheme: light; }
    .receipt { background: #fff; max-width: 540px; margin: 0 auto; padding: 28px 32px; border-radius: 14px; box-shadow: 0 6px 24px rgba(0,0,0,0.08); }
    .receipt-header { text-align: center; border-bottom: 2px solid #1a2622; padding-bottom: 14px; margin-bottom: 18px; }
    /* Phase 31 (S29 Funktionstest-Fix): Logo-Begrenzung im Standalone-CSS,
       sonst rendert das Bild in nativer Auflösung (~1024px). */
    .receipt-logo { display: block; max-width: 80px; max-height: 60px; width: auto; height: auto; margin: 0 auto 8px auto; object-fit: contain; }
    .receipt-brand { font-family: cursive; font-size: 1.9rem; color: #2f6b3a; }
    .receipt-legal-name { font-weight: 600; margin-top: 4px; }
    .receipt-addr, .receipt-meta-row { font-size: 0.84rem; color: #5b6660; }
    .receipt-tax-id { font-size: 0.78rem; color: #5b6660; margin-top: 4px; }
    .receipt-meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; margin: 14px 0; font-size: 0.86rem; }
    .receipt-label { display: block; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.06em; color: #5b6660; }
    .receipt-value { font-weight: 600; }
    .receipt-items-table { width: 100%; border-collapse: collapse; font-size: 0.86rem; margin: 14px 0; }
    .receipt-items-table th, .receipt-items-table td { padding: 4px 6px; border-bottom: 1px dashed #ddd; text-align: left; }
    .receipt-items-table .num, .receipt-items-table th.num, .receipt-items-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
    .receipt-tax-table { width: 100%; font-size: 0.86rem; margin-top: 8px; }
    .receipt-tax-table td { padding: 2px 4px; }
    .receipt-tax-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
    .receipt-total-row { display: flex; justify-content: space-between; font-weight: 700; font-size: 1.05rem; margin-top: 8px; padding-top: 8px; border-top: 2px solid #1a2622; }
    .receipt-pay-line { margin-top: 8px; font-size: 0.88rem; }
    .receipt-correction-stamp { text-align: center; font-size: 2rem; color: #b43232; border: 3px solid #b43232; padding: 4px 0; margin: 12px 0 18px; border-radius: 6px; transform: rotate(-1.5deg); font-weight: 800; letter-spacing: 0.08em; }
    .receipt-correction-meta { background: rgba(180,50,50,0.05); border-left: 3px solid #b43232; padding: 12px 14px; border-radius: 4px; margin: 16px 0 18px; }
    .receipt-correction-headline { font-weight: 700; color: #b43232; margin-bottom: 8px; }
    .receipt-correction-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; font-size: 0.86rem; }
    .receipt-correction-hint { font-size: 0.72rem; opacity: 0.7; margin-top: 10px; font-style: italic; }
    .receipt-corrected-banner { text-align: center; background: rgba(180,50,50,0.08); color: #8b2828; border: 1px dashed #b43232; padding: 6px 10px; border-radius: 4px; margin: 0 0 14px; font-size: 0.82rem; }
    .receipt-tse { background: rgba(0,0,0,0.04); padding: 10px 12px; border-radius: 4px; margin-top: 12px; font-size: 0.82rem; }
    .receipt-tse-headline { margin-bottom: 6px; font-weight: 600; }
    .receipt-tse-badge { display: inline-block; background: #3c8c50; color: #fff; padding: 1px 5px; border-radius: 3px; font-size: 0.66rem; margin-right: 4px; }
    .receipt-tse-mock .receipt-tse-badge { background: #d97a2c; }
    .receipt-tse-pending .receipt-tse-badge { background: #b43232; }
    .receipt-tse-payload { font-family: ui-monospace, monospace; font-size: 0.66rem; background: rgba(0,0,0,0.04); padding: 6px 8px; border-radius: 3px; word-break: break-all; white-space: pre-wrap; max-height: 100px; overflow: auto; }
    .receipt-tse-table { width: 100%; font-size: 0.78rem; }
    .receipt-tse-table th { text-align: left; padding: 2px 6px; opacity: 0.7; font-weight: 600; }
    .receipt-tse-table td { padding: 2px 6px; font-variant-numeric: tabular-nums; }
    /* S54 — visueller QR-Block */
    .receipt-tse-qr { display: flex; align-items: center; justify-content: center; background: #fff; padding: 8px; border-radius: 4px; margin: 8px auto; min-width: 120px; min-height: 120px; border: 1px solid #ccc; }
    .receipt-tse-qr-placeholder { font-size: 0.74rem; color: #5a6660; font-style: italic; padding: 30px 0; }
    .receipt-tse-qr .qr-code-svg { width: 100%; height: auto; max-width: 160px; display: block; image-rendering: pixelated; }
    .receipt-tse-qr-hint { font-size: 0.7rem; color: #5a6660; text-align: center; margin: 2px 0 6px; }
    .receipt-tse-payload-details { margin: 6px 0 4px; font-size: 0.74rem; }
    .receipt-tse-payload-details summary { cursor: pointer; color: #5a6660; padding: 2px 0; }
    .receipt-footer { margin-top: 14px; font-size: 0.78rem; color: #5b6660; }
    .receipt-print-meta { text-align: right; font-size: 0.7rem; color: #7a827d; margin-top: 6px; }
    .receipt-empty { text-align: center; padding: 20px; color: #7a827d; }
    .receipt-tax-table td.num + td .receipt-empty { padding: 4px; }
    @media print { body { background: #fff; } .receipt { box-shadow: none; } }
  `;
  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><meta name="color-scheme" content="light only"><meta name="supported-color-schemes" content="light"><title>${htmlEscape(title)}</title><style>${css}</style></head><body>${body}</body></html>`;
}

